/**
 * ⭐⭐ WHETHER THE LANDLORD CARRIES ANYTHING - and the OM does not always itemise it.
 *
 * 🚨 THE DEFECT THIS EXISTS FOR. 258 Bernard Street, Bakersfield, run through the real front door
 * on 2026-08-30. The OM prints no expense schedule at all, so the extractor DERIVED the landlord's
 * cost from the two figures the document does print - `operating_expenses (from EGI - noi)`,
 * $361,075 of rent against a $351,000 marketed NOI - and wrote a note naming it: *"difference of
 * approximately $10,075 presumably attributable to landlord-paid property insurance"*. The OM says
 * the same thing in prose: the tenant pays CAM and taxes, the landlord carries property insurance,
 * roof and structure.
 *
 * ⛔ THE ANALYZER MODELLED ZERO. The seeding block walked the ITEMISED array, that array was empty,
 * and the whole block - including the plug that carries a stated total - sat behind a
 * `lineItems.length > 0` guard. So a figure the app derived, stored, and printed on the property
 * header never reached the model that prices the deal. **A field that saves and never loads**,
 * the same shape as `close_date` and as an endpoint with no caller. Cost: cap 6.94% against a
 * delivered 6.75%, 0.19pp, in the direction that flatters the deal.
 *
 * ⭐ THE RULE. "Pure NNN" is a statement about what the LANDLORD PAYS, not about what the lease is
 * labelled. A property carrying a positive operating-expense total is not pure NNN however its
 * lease type reads - the number is the evidence and it outranks the label.
 *
 * ⭐ Joseph's rulebook already draws the line: *"When pure NNN with $0 landlord expenses, show $0
 * expenses AND don't add reimbursements to income."* The condition was always "$0 landlord
 * expenses"; only the test for it was wrong.
 */
export interface LandlordExpenseInput {
  /** The lease type on the analysis reads as full triple-net. */
  isFullNNN: boolean;
  /** The stated income already has reimbursements inside it, so gross expenses must be shown. */
  incomeIncludesReimbursements: boolean;
  /** How many line items the OM's own expense schedule carried. Zero is common and normal. */
  lineItemCount: number;
  /** `property.operating_expenses` - stated by the OM, or derived by the extractor as EGI - NOI. */
  statedTotal: number | null | undefined;
}

export interface LandlordExpenseBasis {
  /** Model zero landlord expenses, and do not add reimbursements to income. */
  pureNNN: boolean;
  /**
   * What to carry as the unallocated expense line when the OM itemised nothing.
   * `null` means there is nothing to carry - either it is genuinely pure NNN, or the line items
   * already account for it and the existing per-category seeding governs.
   */
  otherPlug: number | null;
  /** Said on screen, because a derived expense printed as a fact is how a guess becomes one. */
  why: string;
}

const positive = (v: unknown): v is number => typeof v === 'number' && isFinite(v) && v > 0;

export const landlordExpenseBasis = (input: LandlordExpenseInput): LandlordExpenseBasis => {
  const { isFullNNN, incomeIncludesReimbursements, lineItemCount, statedTotal } = input;
  const carriesCost = positive(statedTotal);

  if (lineItemCount > 0) {
    // The OM itemised. Per-category seeding governs, and its own stated-total plug already runs.
    return { pureNNN: false, otherPlug: null, why: `${lineItemCount} expense line items from the OM.` };
  }

  if (carriesCost) {
    return {
      pureNNN: false,
      otherPlug: statedTotal as number,
      why:
        `The OM itemises no expenses, but the property carries $${Math.round(statedTotal as number).toLocaleString('en-US')} ` +
        `of operating expense - so the landlord is not net of everything and this is not a pure NNN basis. ` +
        `It is carried as an unallocated expense; split it into real lines once the operating statements arrive.`,
    };
  }

  if (isFullNNN && !incomeIncludesReimbursements) {
    return { pureNNN: true, otherPlug: null, why: 'Pure NNN: no itemised expenses and no stated total, so the landlord carries nothing.' };
  }

  return { pureNNN: false, otherPlug: null, why: 'No expense evidence either way - the screen\'s own defaults govern.' };
};
